import { useMemo } from "react";
import { find, map } from "lodash";
import { getPlatformColor } from "@linkfluence/models/platform";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";

import {
  InfluencerLensFactory,
  SearchableInfluencer,
} from "@radarly/models/influencer";
import {
  getCountFromSort,
  getMetricFromSort,
} from "@radarly/models/influencer/generic";
import { SearchFragmentUsers, SortInfluencer } from "@radarly/models/search";
import { useRangeFromFragment } from "./useRangeFromFragment";

function formatAuthor(influencer: SearchableInfluencer, sort: SortInfluencer) {
  const influencerLens = InfluencerLensFactory.create(influencer.user);
  return {
    id: influencerLens.getUID(),
    weight: getCountFromSort(influencer, sort),
    label: influencerLens.getName(),
    color: getPlatformColor(influencer.user.platform),
  };
}

export function useCloudAuthorsWidget(
  data: SearchFragmentUsers["data"],
  fragment: SearchFragmentUsers,
  onClickAuthor: (author: SearchableInfluencer) => void
) {
  const locale = useCurrentUserLocale();
  const range = useRangeFromFragment(fragment, false);
  const sort = fragment.request.aggs.users.sort.on;
  const authors = useMemo(() => {
    return map(data ? data.aggs.users.values : [], (influencer) => {
      return formatAuthor(influencer, sort);
    });
  }, [data, sort]);
  const _onClickAuthor = (uid: string | number) => {
    const findedInfluencer = find(data.aggs.users.values, (influencer) => {
      const influencerLens = InfluencerLensFactory.create(influencer.user);
      return influencerLens.getUID() === uid;
    });
    if (findedInfluencer) {
      onClickAuthor(findedInfluencer);
    }
  };
  return {
    authors,
    locale,
    range,
    metric: getMetricFromSort(sort),
    limit: 30,
    order: "label" as const,
    onClickAuthor: onClickAuthor ? _onClickAuthor : null,
  };
}
